import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs/Rx";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime } from "rxjs/operator/debounceTime";
import { Community } from "../shared/services/community/community";
import { CommunityService } from "../shared/services/community/community.service";
import { FileUploadService } from "../shared/services/fileuploadservice";
import { LoaderService } from "../shared/services/loader.service";
import { StringGenerator } from "./../shared/string/generator";
import { BackendService } from "../shared/services/backend.service";
import { AuthService } from "../shared/services/auth.service";
import { CountryService } from "../shared/location/country.service";
import { StateService } from "../shared/location/state.service";
import { CityService } from "../shared/location/city.service";

@Component({
  selector: "app-community-management",
  templateUrl: "./community-management.component.html",
  styleUrls: ["./community-management.component.css"]
})
export class CommunityManagementComponent implements OnInit, OnDestroy {
  private routeSubscription: Subscription;
  private alertSubscription: Subscription = new Subscription();
  private _alert: Subject<string> = new Subject<string>();
  activeRouteIndex: number;
  communities = [];
  community: Community;
  successMessage = "";
  errorMessage = "";
  modalReference: NgbModalRef;
  page = 1;
  deletedCommunity: string;
  hasSelectedState = false;
  selectedCountryCode: string;
  seletedState: any = { name: "", code: 0 };
  seletedState2: any = { name: "", code: 0 };
  selectedLGA: any = { name: "", code: 0 };
  street: any = { name: "" };
  hasNoName = false;
  hasNoStreet = false;
  hasNoSubAccountCode = false;
  private uploadedAvatarURL$;
  private uploadedAvatarURLSubscription;
  query = "";
  confirmationMessage: string;
  confirmationTitle: string;
  actionType = "";
  reference: string;
  operatorId: string;
  defaultCountry = "NG";
  countryCode: string;
  countries = [];
  states = [];
  cities = [];
  selectedStateId: string;
  selectedAddress: {
    countryCode: string, stateId: string, lgaId: string, street: string
  };

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private communityService: CommunityService,
    private loaderService: LoaderService,
    private uploadService: FileUploadService,
    private stringGenerator: StringGenerator,
    private countryService: CountryService,
    private stateService: StateService,
    private cityService: CityService
  ) {
    this.selectedAddress = {
      countryCode: "", stateId: "", lgaId: "", street: ""
    };
  }

  ngOnInit(): void {
    this.stringGenerator.generate("community").then(reference => (this.reference = reference));

    this.loaderService.startLoader();
    this.routeSubscription = this.route.params.subscribe(params => (this.activeRouteIndex = params.index));
    this.route.queryParams.first().subscribe(params => {
      if (params.isSuccess === "true" || params.isSuccess === "false") {
        const msgType = params.isSuccess === "true" ? true : false;
        const msg = msgType ? "Community successfully deleted" : "Community could not be deleted";
        this.alertSubscription = this._alert.subscribe(
          message => (msgType ? (this.successMessage = message) : (this.errorMessage = message))
        );
        debounceTime
          .call(this._alert, 3000)
          .subscribe(() => (msgType ? (this.successMessage = undefined) : (this.errorMessage = undefined)));
        this._alert.next(msg);
        this.alertSubscription.unsubscribe();
      }
    });

    this.countryService.getCountries().subscribe(countries => {
      if (countries) {
        this.countries = [];
        countries.forEach(country => {
          this.countries.push({ code: country.iso_3166_1_alpha2, name: country.name.common });
        });
      }
    }
    );

    this.community = new Community();
    this.communityService.getCommunities().subscribe(
      communities => {
        communities.sort((a, b) => {
          const nameA = a.name.toUpperCase(); // ignore upper and lowercase
          const nameB = b.name.toUpperCase(); // ignore upper and lowercase
          const result = nameA < nameB ? -1 : nameA > nameB ? 1 : 0;

          return result;
        });

        this.communities = communities;
      }
    );

    this.operatorId = BackendService.generateUserId(this.authService.getAuthState().email);
    this.loaderService.stopLoader();
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.routeSubscription.unsubscribe();
    this.uploadedAvatarURLSubscription ? this.uploadedAvatarURLSubscription.unsubscribe() : null;
  }

  createCommunity(modal): void {
    this.hasNoName = this.hasNoStreet = this.hasNoSubAccountCode = false;
    this.community = new Community();
    this.openModal(modal, "md");
  }

  openImageUploader(): void {
    const element = document.getElementById("imageUploader") as HTMLElement;
    element.click();
  }

  saveCommunity(): void {
    this.community.name = this.community.name.trim();
    this.selectedAddress.street = this.street.name.trim();

    if (!this.community.name) {
      this.hasNoName = true;

      return;
    }
    if (!this.selectedAddress.street) {
      this.hasNoStreet = true;

      return;
    }

    if (!this.community.subAccountCode) {
      this.hasNoSubAccountCode = true;

      return;
    }

    if (!this.reference) {
      alert("Invalid community reference, please try again !");

      return;
    }

    if (!this.selectedAddress.countryCode || !this.selectedAddress.stateId || !this.selectedAddress.lgaId) {
      alert("Please fill in all required fields !");

      return;
    }

    this.community.location = { address: this.selectedAddress };
    this.community.reference = this.reference;
    this.community.operatorId = this.operatorId;
    this.communityService.create(this.community);

    this.closeModal();
    this.communityService.getCommunities().subscribe(
      communities => {
        communities.sort((a, b) => {
          const nameA = a.name.toUpperCase(); // ignore upper and lowercase
          const nameB = b.name.toUpperCase(); // ignore upper and lowercase
          const result = nameA < nameB ? -1 : nameA > nameB ? 1 : 0;

          return result;
        });

        this.communities = communities;
      }
    );
    this.showAlert(true, "Community created successfully.");
  }

  setAddress(lgaId: string): void {
    if (this.seletedState) {
      this.selectedAddress.countryCode = this.selectedCountryCode;
      this.selectedAddress.stateId = this.selectedStateId;
      this.selectedAddress.lgaId = lgaId;
      this.selectedAddress.street = this.street.name;
    }
  }

  uploadImage(event): void {
    const file = event.target.files[0];
    this.uploadedAvatarURL$ = this.uploadService.upload("avatars", file);
    this.uploadedAvatarURLSubscription = this.uploadedAvatarURL$.subscribe(data => {
      this.community.avatar = data;
    });
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  closeModal(): void {
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  showAlert(msgType, msg): void {
    this.alertSubscription = this._alert.subscribe(
      message => (msgType ? (this.successMessage = message) : (this.errorMessage = message))
    );
    debounceTime
      .call(this._alert, 3000)
      .subscribe(() => (msgType ? (this.successMessage = undefined) : (this.errorMessage = undefined)));
    this._alert.next(msg);
    this.alertSubscription.unsubscribe();
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.openModal(modal, "sm");
  }

  confirmAction(type, community: any, modal, event: any): void {
    event.stopPropagation();
    this.community = community;
    this.actionType = type;
    const message = `Are you sure you want to ${type} ${this.community.name}`;
    this.confirm("Attention!", message, modal);
  }

  deactivateCommunity(): void {
    this.communityService.deactivate(this.community.id);
    this.closeModal();
    this.showAlert(true, "Community deactivated successfully.");
  }

  activateCommunity(): void {
    this.communityService.activate(this.community.id);
    this.closeModal();
    this.showAlert(true, "Community activated successfully.");
  }

  downloadCommunities(): void {
    this.communityService.getCommunities().subscribe(communities => {
      communities = communities.map((community, index) => {
        return { "S/N": ++index, Name: community["name"] };
      });
      BackendService.downloadEntities(communities, "communities");
    });
  }

  fetchStatesOnCountryChange(countryCode: string) {
    this.selectedCountryCode = countryCode;
    this.states = [];
    this.cities = [];
    if (this.selectedCountryCode) {
      this.stateService.getCountryStates(this.selectedCountryCode)
        .subscribe(states => {
          if (states) {
            const keys = Object.keys(states);
            keys.forEach(key => {
              this.states.push({ id: key, name: states[key] });
            });
          }
        });
    }
  }

  fetchCitiesOnStateChange(stateId: string) {
    this.cities = [];
    this.selectedStateId = stateId;
    if (this.selectedStateId) {
      this.cityService.getStateCities(this.selectedCountryCode, stateId)
        .subscribe(countrySnap => {
          if (countrySnap) {
            if (stateId) {
              const stateSnap = countrySnap;
              const stateKeys = Object.keys(stateSnap);
              stateKeys.forEach(key => {
                this.cities.push({ id: key, name: stateSnap[key] });
              });
            }
          }
        });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/community-management/community-management.component.ts